import { AssetPathEncodeState } from '@gnsx/genesys.js';

import { AbstractProvider } from './abstract-provider';
import { StorageProviderType } from './interfaces';

import type { FileChangeEvent, FileDownloadOptions, FileExistsOptions } from './interfaces';
import type { AssetPath, FileItem, FileListResult , FileUploadOptions} from '@gnsx/genesys.js';

/**
 * S3 Storage provider implementation for client-side operations.
 * Uses API routes for operations that require credentials (list, delete, metadata).
 * Uses public URLs for reads (games/* is publicly accessible).
 * Uses presigned URLs for uploads.
 */
export class S3StorageProvider extends AbstractProvider {
  private bucketUrl: string;

  constructor() {
    super();
    // Public S3 URL pattern - bucket and region from env
    const bucket = process.env.NEXT_PUBLIC_S3_BUCKET_NAME ?? '';
    const region = process.env.NEXT_PUBLIC_AWS_REGION ?? 'us-east-1';
    this.bucketUrl = `https://${bucket}.s3.${region}.amazonaws.com`;
  }

  get providerType(): StorageProviderType {
    return StorageProviderType.S3;
  }

  requireProjectContext(): boolean {
    return true;
  }

  private constructPublicUrl(path: AssetPath): string {
    return `${this.bucketUrl}/${path.initialPath}`;
  }

  async doListFiles(path: AssetPath, _recursive: boolean): Promise<FileListResult> {
    // Extract gameId from path (expected format: games/{gameId}/...)
    const pathParts = path.initialPath.split('/');
    if (pathParts[0] !== 'games' || !pathParts[1]) {
      throw new Error('S3StorageProvider: Invalid path format, expected games/{gameId}/...');
    }
    const gameId = pathParts[1];

    // Call API route to list files
    const response = await fetch(`/api/games/${gameId}/assets/list`);
    if (!response.ok) {
      throw new Error(`Failed to list files: ${response.status} ${response.statusText}`);
    }

    const { files } = (await response.json()) as { files: string[] };

    // Filter to only files under the requested path
    const prefix = path.initialPath.replace(`games/${gameId}/`, '');
    const filteredFiles = files.filter((f) => f.startsWith(prefix));

    // Convert to FileItem format
    const fileItems: FileItem[] = filteredFiles.map((filePath) => ({
      name: filePath.split('/').pop() ?? filePath,
      path: `games/${gameId}/${filePath}`,
      absolutePath: `games/${gameId}/${filePath}`,
      size: 0, // Size not available from list
      modifiedTime: new Date(),
      isDirectory: false,
    }));

    // Extract directories from file paths
    const dirSet = new Set<string>();
    for (const filePath of filteredFiles) {
      const parts = filePath.split('/');
      if (parts.length > 1) {
        dirSet.add(parts[0]);
      }
    }

    const directories: FileItem[] = Array.from(dirSet).map((dirName) => ({
      name: dirName,
      path: `games/${gameId}/${prefix}${dirName}`,
      absolutePath: `games/${gameId}/${prefix}${dirName}`,
      size: 0,
      modifiedTime: new Date(),
      isDirectory: true,
    }));

    return { files: fileItems, directories };
  }

  async uploadFile(
    path: AssetPath,
    content: Blob | File | string | ArrayBuffer,
    options?: FileUploadOptions
  ): Promise<{ path: string; name: string }> {
    // Extract gameId from path
    const pathParts = path.initialPath.split('/');
    if (pathParts[0] !== 'games' || !pathParts[1]) {
      throw new Error('S3StorageProvider: Invalid path format, expected games/{gameId}/...');
    }
    const gameId = pathParts[1];
    const filePath = pathParts.slice(2).join('/');

    // Get presigned URL from API
    const signedUrlResponse = await fetch(`/api/games/${gameId}/assets/signed-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filePath,
        contentType: options?.contentType ?? 'application/octet-stream',
        action: 'write',
      }),
    });

    if (!signedUrlResponse.ok) {
      throw new Error(`Failed to get signed URL: ${signedUrlResponse.status}`);
    }

    const { signedUrl } = (await signedUrlResponse.json()) as { signedUrl: string };

    // Convert content to Blob if necessary
    let blob: Blob;
    if (typeof content === 'string') {
      blob = new Blob([content], { type: options?.contentType ?? 'text/plain' });
    } else if (content instanceof ArrayBuffer) {
      blob = new Blob([content], { type: options?.contentType ?? 'application/octet-stream' });
    } else {
      blob = content;
    }

    // Upload to S3 using presigned URL
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('Content-Type', options?.contentType ?? 'application/octet-stream');

      if (options?.onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            options.onProgress?.(progress);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed: network error'));
      xhr.send(blob);
    });

    return {
      path: path.initialPath,
      name: path.initialPath.split('/').pop() ?? '',
    };
  }

  async downloadFile(
    path: AssetPath,
    options?: FileDownloadOptions
  ): Promise<Blob | string | ArrayBuffer> {
    const url = this.constructPublicUrl(path);

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }

    if (options?.asBlob) {
      return await response.blob();
    }

    const textContent = await this.extractTextContent(response, path.initialPath);
    if (textContent !== null) {
      return textContent;
    }

    return await response.arrayBuffer();
  }

  async deleteItem(path: AssetPath, _recursive = true): Promise<void> {
    // Extract gameId from path
    const pathParts = path.initialPath.split('/');
    if (pathParts[0] !== 'games' || !pathParts[1]) {
      throw new Error('S3StorageProvider: Invalid path format, expected games/{gameId}/...');
    }
    const gameId = pathParts[1];
    const filePath = pathParts.slice(2).join('/');

    // Get presigned delete URL from API
    const signedUrlResponse = await fetch(`/api/games/${gameId}/assets/signed-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filePath,
        contentType: 'application/octet-stream',
        action: 'delete',
      }),
    });

    if (!signedUrlResponse.ok) {
      throw new Error(`Failed to get signed URL: ${signedUrlResponse.status}`);
    }

    const { signedUrl } = (await signedUrlResponse.json()) as { signedUrl: string };

    // Delete using presigned URL
    const deleteResponse = await fetch(signedUrl, { method: 'DELETE' });
    if (!deleteResponse.ok && deleteResponse.status !== 404) {
      throw new Error(`Failed to delete: ${deleteResponse.status}`);
    }
  }

  async createDirectory(_path: AssetPath): Promise<void> {
    // S3 doesn't have real directories - they're virtual based on key prefixes
    // No-op is fine here
  }

  async exists(path: AssetPath, _options?: FileExistsOptions): Promise<boolean> {
    const url = this.constructPublicUrl(path);

    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  async resolvePath(path: AssetPath, _expiry?: number): Promise<AssetPath> {
    if (path.isResolved()) {
      return path;
    }
    const url = this.constructPublicUrl(path);
    path.resolvePath(url, AssetPathEncodeState.Encoded);
    return path;
  }

  onFileSystemChange(_callback: (event: FileChangeEvent) => void): () => void {
    // S3 doesn't support real-time file system monitoring
    console.warn('File system change monitoring is not supported in S3 Storage');
    return () => {};
  }
}
